import {
	BusyIndicator,
	ChangeTriggerKind,
	SearchInputTrigger
} from "client/Components";
import { AddressBookContactSource } from "client/Features/AddressBook/Overview/Overview";
import { ContactListItem, ContactPresenceStatus } from "client/Api/Email/contacts";
import { ReactElement, useCallback, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { LiveTileContent } from "../../LiveTileContent";
import { Contact } from "./Contact/Contact";
import { ContactDialog } from "../../../../AddressBook/ContactDialog";
import { RemoteDataSourceState } from "client/Hooks";
import { Guid } from "client_legacy/ndaw/guid";
import { isNotNilOrEmpty } from "client/Misc/utils";

const {
	ContentStack,
	SearchInput,
	Scrollable,
	List,
	BusyIndicatorContainer,
	LoadMoreButton,
	NoResults
} = LiveTileContent;

export type GenericContactsTabProps = {
	contacts: readonly ContactListItem[];
	loadingState: RemoteDataSourceState;
	searchTerm: VersionedValue<string>;
	onSearchTermChange(newValue: string, version: Guid): void;
	source: AddressBookContactSource;
	hasMore: boolean;
	onLoadMore(): void;
	onRetry(): void;
	onListItemClick(): void;
	presenceStatuses?: readonly ContactPresence[];
};

export const GenericContactsTab = (props: GenericContactsTabProps): ReactElement => {
	const {
		contacts,
		loadingState,
		searchTerm,
		onSearchTermChange,
		source,
		hasMore,
		onLoadMore,
		onRetry,
		onListItemClick,
		presenceStatuses
	} = props;

	const [contactDetails, setContactDetails] = useState<ContactDetailsState>({ isOpen: false });

	const getContactPresenceStatus = (contactId: string): ContactPresenceStatus => {
		if (!presenceStatuses) {
			return ContactPresenceStatus.NotApplicable;
		}

		const contactPresence: ContactPresence | undefined = presenceStatuses
			.find(p => p.contactId === contactId);

		if (!contactPresence) {
			return ContactPresenceStatus.NotApplicable;
		}

		return contactPresence.status;
	};

	const openContactDetailsHandler = useCallback(
		(contactId: string, source: AddressBookContactSource): void => {
			onListItemClick();
			setContactDetails({
				isOpen: true,
				contactId,
				source
			});
		},
		[onListItemClick]
	);

	const renderResult = (): ReactElement | null => {
		if (loadingState === "error") {
			return (
				<NoResults
					icon="gear"
					title={localize("_NotAbleToRetrieveContacts")}
					message={localize("_WeWereUnableToRetrieveYourContacts")}
					onRetry={onRetry}
				/>
			);
		}

		if (contacts.length === 0) {
			if (loadingState === "loading") {
				return null;
			}

			if (isNotNilOrEmpty(searchTerm.value)) {
				return (
					<NoResults
						icon="magnifying-glass"
						title={localize("_NoSearchResults")}
						message={localize("_PleaseTrySearchingWithAnotherTerm")}
					/>
				);
			}

			return (
				<NoResults
					icon="address-book"
					title={localize("_NoContactsYet")}
					message={localize("_ThereAreNoContactsYetInThisAddressBook")}
				/>
			);
		}

		return (
			<>
				<Scrollable>
					<List trackingId="contacts-list">
						{contacts.map(c => (
							<Contact
								key={c.id}
								contact={c}
								source={source}
								onOpenContact={openContactDetailsHandler}
								presenceStatus={getContactPresenceStatus(c.id)}
							/>
						))}
					</List>
					{hasMore && (
						<LoadMoreButton onClick={onLoadMore} />
					)}
				</Scrollable>
			</>
		);
	};

	return (
		<>
			<ContentStack>
				<SearchInput
					value={searchTerm}
					onChange={onSearchTermChange}
					trigger={contactSearchTrigger}
					trackingId="search-input"
					placeholder={localize("_Search")}
				/>
				<BusyIndicatorContainer>
					{loadingState === "loading" && (
						<BusyIndicator />
					)}
					{renderResult()}
				</BusyIndicatorContainer>
			</ContentStack>
			{contactDetails.isOpen && (
				<ContactDialog
					source={contactDetails.source}
					initialView={{
						kind: "details",
						contactId: contactDetails.contactId
					}}
					onClose={() => setContactDetails({ isOpen: false })}
				/>
			)}
		</>
	);
};
GenericContactsTab.displayName = "ContactList";

export type ContactPresence = {
	contactId: string;
	status: ContactPresenceStatus;
};

const contactSearchTrigger: SearchInputTrigger = { kind: ChangeTriggerKind.Debounced };

type ContactDetailsState = {
	isOpen: true;
	source: AddressBookContactSource;
	contactId: string;
} | {
	isOpen: false;
	source?: undefined;
	contactId?: string;
};